import { Fragment, useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { clearMessages } from 'store/ancillary/actions';
import { raiseErrorBanner } from 'components/MessageDispatch/messageDispatch';
import DomPurify from 'dompurify';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import ClearIcon from '@mui/icons-material/Clear';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { JobCategoryDetails } from './JobCategoryDetails';
import { getJobCategory, search } from '../_api';
import { debounceAsync } from 'utils/misc/debounce';
import { OBJ_APP_BRIDGES } from '../_constants';

const JobCategoryDirectory = ({
	selection = null,
	systemID = OBJ_APP_BRIDGES.ID,
	...props
}) => {
	const dispatch = useDispatch();
	const refTextField = useRef(null);
	const [sArrJobCategories, setArrJobCategories] = useState([]);
	const [sIsResultsExpanded, setIsResultsExpanded] = useState(false);
	const [sLoading, setLoading] = useState(false);
	const [sSelection, setSelection] = useState(selection);
	const [sSearchValue, setSearchValue] = useState(selection?.name ?? '');

	const DEFAULT_LIST_ITEM = useMemo(() => {
		return { id: -1, name: 'No results' };
	}, []);

	const debouncedChange = useMemo(() => {
		return debounceAsync(async value => {
			try {
				if (value) {
					setLoading(true);
					setIsResultsExpanded(false);
					const arrMatches = await search(
						DomPurify.sanitize(value),
						systemID,
					);
					if (arrMatches.length > 0) {
						setArrJobCategories(arrMatches);
					} else {
						setArrJobCategories([DEFAULT_LIST_ITEM]);
					}
				} else {
					setArrJobCategories([DEFAULT_LIST_ITEM]);
				}
			} catch (ex) {
				dispatch(clearMessages());
				raiseErrorBanner(ex, dispatch);
			} finally {
				setIsResultsExpanded(true);
				setLoading(false);
			}
		});
	}, [DEFAULT_LIST_ITEM, dispatch, systemID]);

	const onClick = useCallback(
		async id => {
			try {
				const selectedJobCategory = await getJobCategory(id);
				setSelection(selectedJobCategory);
			} catch (ex) {
				dispatch(clearMessages());
				raiseErrorBanner(ex, dispatch);
			}
		},
		[dispatch],
	);

	return (
		<Fragment key={props.key}>
			<Typography
				variant='h6'
				color='primary'
				align='center'
				gutterBottom
			>
				Epic Job Category Directory
			</Typography>
			<Typography variant='body2' align='left' gutterBottom>
				To index Epic job categories and their attributes, search below
				by name or keyword.
			</Typography>
			<TextField
				inputRef={refTextField}
				hiddenLabel
				fullWidth
				InputProps={{
					endAdornment: sLoading ? (
						<InputAdornment position='end'>
							<CircularProgress
								size={24}
								variant='indeterminate'
							/>
						</InputAdornment>
					) : sSearchValue !== '' ? (
						<InputAdornment position='end'>
							<IconButton
								edge='end'
								size='small'
								onClick={() => {
									setSearchValue('');
									refTextField.current?.focus();
								}}
							>
								<ClearIcon />
							</IconButton>
						</InputAdornment>
					) : null,
					value: sSearchValue,
				}}
				label='Search'
				onChange={e => {
					setSearchValue(e.target?.value);
					debouncedChange(e.target?.value);
				}}
				placeholder='name or keywords'
				variant='standard'
			/>
			{sArrJobCategories.length > 0 && (
				<Accordion
					sx={{
						borderRadius: '8px',
						marginTop: '32px',
						'&:before': {
							backgroundColor: 'unset',
						},
					}}
					disableGutters
					elevation={2}
					expanded={sIsResultsExpanded}
					onChange={() => setIsResultsExpanded(state => !state)}
					square={false}
				>
					<AccordionSummary
						sx={{ fontFamily: 'default', fontWeight: 'bold' }}
						expandIcon={<ExpandMoreIcon />}
					>
						Search Results
					</AccordionSummary>

					<AccordionDetails
						sx={{
							padding: '0px 8px 8px 8px',
						}}
					>
						<List
							sx={{
								maxHeight: '200px',
								overflowY: 'auto',
								paddingTop: '0px',
								paddingBottom: '0px',
							}}
							dense
						>
							{sArrJobCategories.map((jc, index) => (
								<ListItemButton
									disableGutters
									divider={
										index !== sArrJobCategories.length - 1
									}
									key={jc.id}
									disabled={jc.id === DEFAULT_LIST_ITEM.id}
									onClick={() => onClick(jc.id)}
									selected={jc.id === sSelection?.id}
								>
									<ListItemText primary={jc.name} />
								</ListItemButton>
							))}
						</List>
					</AccordionDetails>
				</Accordion>
			)}

			{sSelection && (
				<JobCategoryDetails
					key={sSelection.id}
					jobCategory={sSelection}
				/>
			)}
		</Fragment>
	);
};

export { JobCategoryDirectory };
